import React from "react"

// Simple utility to combine classes
const cn = (...inputs: (string | undefined | null | false)[]): string => {
  return inputs.filter(Boolean).join(" ")
}

// Manual variant system to replace CVA
const getVariantClasses = (variant?: string, size?: string): string => {
  const baseClasses = "btn"

  const variantClasses = {
    filled: "btn-primary",
    "filled-danger": "btn-danger",
    outline: "btn-outline-primary",
    "outline-danger": "btn-outline-danger",
    ghost: "btn-ghost",
  }

  const sizeClasses = {
    sm: "btn-sm",
    md: "btn-md",
    lg: "btn-lg",
  }

  const variantClass =
    variantClasses[variant as keyof typeof variantClasses] || "btn-primary"
  const sizeClass = sizeClasses[size as keyof typeof sizeClasses] || "btn-md"

  return cn(baseClasses, variantClass, sizeClass)
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
  variant?: "filled" | "filled-danger" | "outline" | "outline-danger" | "ghost"
  size?: "sm" | "md" | "lg"
  icon?: React.ReactNode
  iconPosition?: "left" | "right"
  loading?: boolean
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "filled",
      size = "md",
      children,
      icon,
      iconPosition = "left",
      loading = false,
      loadingText,
      disabled,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading
    const buttonClasses = getVariantClasses(variant, size)

    // Check if this is an icon-only button (no children or empty children)
    const isIconOnly =
      !children || (typeof children === "string" && children.trim() === "")

    return (
      <button
        className={cn(
          "component-ui-button",
          buttonClasses,
          isIconOnly && "btn-icon-only",
          className,
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <div className="button-icon animate-spin">⟳</div>
            <span className="button-text">{loadingText || children}</span>
          </>
        ) : (
          <>
            {icon && iconPosition === "left" && (
              <span className="button-icon">{icon}</span>
            )}
            {children && <span className="button-text">{children}</span>}
            {icon && iconPosition === "right" && (
              <span className="button-icon">{icon}</span>
            )}
          </>
        )}
      </button>
    )
  },
)

Button.displayName = "Button"

export { Button }
